
import React, { useState, useRef, useEffect } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, User, Mail, Phone, MapPin, MessageSquare, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PricingResult } from "@/hooks/use-pricing";
import { trackEvent } from '@/lib/analytics';
import { HCAPTCHA_SITE_KEY } from '@/lib/hcaptcha';

interface ReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  arrivalDate?: Date;
  departureDate?: Date;
  pricing?: PricingResult | null;
}

interface ReservationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  guestCount: number;
  message: string;
}

const ReservationDialog = ({ open, onOpenChange, arrivalDate, departureDate, pricing }: ReservationDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const { toast } = useToast();



  const form = useForm<ReservationFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      postalCode: "",
      country: "France",
      guestCount: 2,
      message: "",
    },
  });

  const onSubmit = async (data: ReservationFormData) => {
    setIsSubmitting(true);
    
    if (!arrivalDate || !departureDate) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner des dates d'arrivée et de départ.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Vérification requise",
        description: "Veuillez compléter la vérification de sécurité avant de continuer.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const reservationData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address: data.address,
        city: data.city,
        postalCode: data.postalCode,
        country: data.country,
        arrivalDate: arrivalDate.toISOString().split('T')[0],
        departureDate: departureDate.toISOString().split('T')[0],
        guests: data.guestCount,
        message: data.message,
        captchaToken: captchaToken,
      };

      const { data: result, error } = await supabase.functions.invoke('send-reservation-email', {
        body: reservationData,
      });

      if (error) throw error;

      trackEvent('reservation_submitted', {
        from: arrivalDate?.toISOString(),
        to: departureDate?.toISOString(),
        nights: pricing?.nights,
        guests: data.guestCount,
      });

      toast({
        title: "Demande envoyée !",
        description: "Votre demande de réservation a été envoyée avec succès. Vous recevrez un email de confirmation sous peu.",
      });

      form.reset();
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error sending reservation:', error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de l'envoi de votre demande. Veuillez réessayer.",
        variant: "destructive",
      });
      // Reset captcha en cas d'erreur
      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateNights = () => {
    if (arrivalDate && departureDate) {
      const diffTime = departureDate.getTime() - arrivalDate.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const nights = calculateNights();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-none h-[95vh] max-h-none flex flex-col p-0">
        <DialogHeader className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Calendar className="h-4 w-4" />
            Demande de réservation
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-3 py-2 sm:px-4 sm:py-3">
          {/* Résumé de la réservation */}
          {arrivalDate && departureDate && (
            <div className="bg-gray-50 p-2.5 sm:p-3 rounded-lg mb-3 sm:mb-4 space-y-2">
              <h3 className="font-semibold mb-1.5 text-sm sm:text-base">Détails du séjour</h3>
              <div className="space-y-0.5 text-xs sm:text-sm">
                <p>
                  <strong>Arrivée :</strong> {format(arrivalDate, "EEEE d MMMM yyyy", { locale: fr })}
                </p>
                <p>
                  <strong>Départ :</strong> {format(departureDate, "EEEE d MMMM yyyy", { locale: fr })}
                </p>
                <p>
                  <strong>Durée :</strong> {nights} nuit{nights > 1 ? 's' : ''}
                </p>
              </div>
              
              {pricing && !pricing.error && (
                <div className="pt-2 border-t border-gray-200">
                  {pricing.requiresQuote ? (
                    <div className="bg-amber-50 p-2 rounded-lg">
                      <p className="text-xs font-medium text-amber-900">
                        💡 Pour un séjour de plus de 7 nuits hors période estivale, nous vous contacterons avec un devis personnalisé.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <h4 className="font-semibold text-xs">Tarification</h4>
                      <div className="space-y-0.5 text-xs">
                        <div className="flex justify-between">
                          <span>Hébergement ({pricing.nights} nuit{pricing.nights > 1 ? 's' : ''})</span>
                          <span className="font-medium">{pricing.subtotal}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Frais de ménage</span>
                          <span className="font-medium">{pricing.cleaningFee}€</span>
                        </div>
                        <div className="pt-1.5 border-t border-gray-300 flex justify-between font-semibold text-sm">
                          <span>Total</span>
                          <span className="text-green-600">{pricing.total}€</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
              {/* Informations personnelles */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                  <User className="h-3.5 w-3.5" />
                  Informations personnelles
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <FormField
                    control={form.control}
                    name="firstName"
                    rules={{ required: "Le prénom est requis" }}
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-xs sm:text-sm">Prénom *</FormLabel>
                        <FormControl>
                          <Input placeholder="Votre prénom" className="h-8 sm:h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    rules={{ required: "Le nom est requis" }}
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-xs sm:text-sm">Nom *</FormLabel>
                        <FormControl>
                          <Input placeholder="Votre nom" className="h-8 sm:h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  rules={{ 
                    required: "L'email est requis",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Adresse email invalide"
                    }
                  }}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <Mail className="h-3.5 w-3.5" />
                        Adresse email *
                      </FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="votre.email@exemple.com" className="h-8 sm:h-9 text-sm" {...field} />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  rules={{ required: "Le numéro de téléphone est requis" }}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <Phone className="h-3.5 w-3.5" />
                        Numéro de téléphone *
                      </FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="06 12 34 56 78" className="h-8 sm:h-9 text-sm" {...field} />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Adresse */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                  <MapPin className="h-3.5 w-3.5" />
                  Adresse
                </h3>

                <FormField
                  control={form.control}
                  name="address"
                  rules={{ required: "L'adresse est requise" }}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs sm:text-sm">Adresse *</FormLabel>
                      <FormControl>
                        <Input placeholder="Numéro et nom de rue" className="h-8 sm:h-9 text-sm" {...field} />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <FormField
                    control={form.control}
                    name="postalCode"
                    rules={{ required: "Le code postal est requis" }}
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-xs sm:text-sm">Code postal *</FormLabel>
                        <FormControl>
                          <Input placeholder="29000" className="h-8 sm:h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    rules={{ required: "La ville est requise" }}
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="text-xs sm:text-sm">Ville *</FormLabel>
                        <FormControl>
                          <Input placeholder="Votre ville" className="h-8 sm:h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="country"
                  rules={{ required: "Le pays est requis" }}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs sm:text-sm">Pays *</FormLabel>
                      <FormControl>
                        <Input placeholder="France" className="h-8 sm:h-9 text-sm" {...field} />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Détails du séjour */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="font-semibold text-sm sm:text-base">Détails du séjour</h3>

                <FormField
                  control={form.control}
                  name="guestCount"
                  rules={{ 
                    required: "Le nombre de voyageurs est requis",
                    min: { value: 1, message: "Au moins 1 voyageur" },
                    max: { value: 6, message: "Maximum 6 voyageurs" }
                  }}
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs sm:text-sm">Nombre de voyageurs *</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min="1" 
                          max="6" 
                          className="h-8 sm:h-9 text-sm"
                          {...field} 
                          onChange={(e) => field.onChange(parseInt(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="flex items-center gap-1.5 text-xs sm:text-sm">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Message (facultatif)
                      </FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Partagez-nous des informations supplémentaires sur votre séjour..."
                          className="min-h-[60px] sm:min-h-[80px] resize-none text-sm"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

            </form>
          </Form>
        </div>

        {/* Vérification de sécurité hCaptcha */}
        <div className="flex-shrink-0 px-3 sm:px-4 pt-2 space-y-1">
          <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
            <Shield className="h-3.5 w-3.5" />
            Vérification de sécurité *
          </h3>
          <div className="hcaptcha-host">
            <HCaptcha
              ref={captchaRef}
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
          </div>
        </div>

        {/* Boutons fixés en bas */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2 sm:px-4 sm:py-3">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 sm:h-10 text-xs sm:text-sm"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={form.handleSubmit(onSubmit)}
              className="h-9 sm:h-10 text-xs sm:text-sm bg-[#86AA8E] hover:bg-opacity-90"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer la demande"}
            </Button>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-1.5">
            * Champs obligatoires
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationDialog;
